import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTopicBySlug } from '../data/businessPlaybookContent';
import StickyTOC from '../components/business-playbook/StickyTOC';
import MobileJumpBar from '../components/business-playbook/MobileJumpBar';
import KeepLearningRow from '../components/business-playbook/KeepLearningRow';
import VideoPlaceholder from '../components/business-playbook/VideoPlaceholder';

// Calculate read time from content string (strip markdown, ~200 wpm)
const getReadTime = (content) => {
  if (!content) return 1;
  const text = content.replace(/\*\*/g, '').replace(/[#\-*>]/g, '');
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

const PlaybookTopic = () => {
  const { topicSlug, articleSlug } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const topic = getTopicBySlug(topicSlug);

  // Determine active article index from URL slug
  const activeIndex = topic && !topic.comingSoon
    ? Math.max(0, topic.sections.findIndex(s => s.id === articleSlug))
    : 0;

  const activeSection = topic?.sections?.[activeIndex] || null;

  // Precompute read times
  const readTimes = topic?.sections
    ? topic.sections.reduce((acc, s) => { acc[s.id] = getReadTime(s.content); return acc; }, {})
    : {};

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top when article changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeIndex]);

  // Navigate to a specific article
  const goToArticle = (sectionId) => {
    navigate(`/business-playbook/${topicSlug}/${sectionId}`, { replace: true });
  };

  const goToIndex = (index) => {
    if (topic?.sections?.[index]) {
      goToArticle(topic.sections[index].id);
    }
  };

  // Topic not found
  if (!topic) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Topic Not Found</h1>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/business-playbook')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/business-playbook')}
            className="text-sm text-purple-600 hover:text-purple-700 cursor-pointer"
          >
            ← Back to Business Playbook
          </span>
        </div>
      </div>
    );
  }

  // Coming soon topic
  if (topic.comingSoon) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/business-playbook')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/business-playbook')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors mb-6 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Business Playbook
          </span>

          <div className="max-w-3xl mx-auto text-center py-12">
            <div className="text-5xl mb-4">{topic.icon}</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{topic.title}</h1>
            <p className="text-sm text-gray-600 mb-6">{topic.subtitle}</p>
            <span className="inline-block px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-full">
              Coming soon
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .playbook-article-content {
          color: #374151;
          font-size: 15px;
          line-height: 1.8;
          max-width: 720px;
        }
        .playbook-article-content p {
          margin-bottom: 16px;
        }
        .playbook-article-content h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-top: 32px;
          margin-bottom: 12px;
        }
        .playbook-article-content strong {
          color: #1f2937;
          font-weight: 600;
        }
        .playbook-article-content ul {
          margin-top: 8px;
          margin-bottom: 16px;
          padding-left: 24px;
          list-style-type: disc;
        }
        .playbook-article-content ul li {
          margin-bottom: 8px;
          line-height: 1.7;
        }
        .playbook-article-content ol {
          margin-top: 8px;
          margin-bottom: 16px;
          padding-left: 24px;
          list-style-type: decimal;
        }
        .playbook-article-content ol li {
          margin-bottom: 8px;
          line-height: 1.7;
        }
        .playbook-article-content a {
          color: #7c3aed;
          text-decoration: none;
          font-weight: 500;
        }
        .playbook-article-content a:hover {
          text-decoration: underline;
        }
        .playbook-article-content a[target="_blank"]::after {
          content: " ↗";
          font-size: 12px;
        }
        .playbook-article-content .playbook-action-link {
          display: block;
          color: #7c3aed;
          font-weight: 500;
          font-size: 14px;
          margin-top: 4px;
          margin-bottom: 4px;
        }
        .playbook-article-content .playbook-disclaimer {
          margin-top: 32px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          font-size: 13px;
          color: #9ca3af;
          line-height: 1.6;
        }
        .playbook-article-content table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 16px;
          margin-bottom: 24px;
          font-size: 14px;
        }
        .playbook-article-content th {
          text-align: left;
          padding: 10px 12px;
          background: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
          font-weight: 600;
          color: #1f2937;
        }
        .playbook-article-content td {
          padding: 10px 12px;
          border-bottom: 1px solid #f3f4f6;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate('/business-playbook')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/business-playbook')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors mb-6 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Business Playbook
        </span>

        {/* Header */}
        <div className="mb-8">
          <div className="text-4xl mb-3">{topic.icon}</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{topic.title}</h1>
          <p className="text-sm text-gray-600">{topic.subtitle}</p>
        </div>

        {/* Mobile Jump Bar */}
        {isMobile && topic.sections.length > 0 && (
          <MobileJumpBar
            sections={topic.sections}
            activeSection={activeSection?.id || ''}
            onSectionClick={goToArticle}
            readTimes={readTimes}
          />
        )}

        {/* Content Layout */}
        <div className="flex gap-8 items-start">
          {/* Desktop Sticky TOC */}
          {!isMobile && topic.sections.length > 0 && (
            <StickyTOC
              sections={topic.sections}
              activeSection={activeSection?.id || ''}
              onSectionClick={goToArticle}
              readTimes={readTimes}
            />
          )}

          {/* Main Content — Single Article */}
          <div className="flex-1 max-w-3xl">
            {activeSection && (
              <section className="bg-white rounded-lg border border-gray-200 p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {activeSection.title}
                </h2>
                {activeSection.videoUrl ? (
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginBottom: '24px'
                  }}>
                    <iframe
                      src={activeSection.videoUrl}
                      style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`Video: ${activeSection.title}`}
                    />
                  </div>
                ) : activeSection.videoPlaceholder ? (
                  <VideoPlaceholder />
                ) : null}
                <div
                  className="playbook-article-content"
                  dangerouslySetInnerHTML={{ __html: activeSection.content }}
                />

                {/* Previous / Next Navigation */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
                  {activeIndex > 0 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => goToIndex(activeIndex - 1)}
                      onKeyDown={(e) => e.key === 'Enter' && goToIndex(activeIndex - 1)}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-purple-600 transition-colors cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous article
                    </span>
                  ) : <div />}
                  {activeIndex < topic.sections.length - 1 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => goToIndex(activeIndex + 1)}
                      onKeyDown={(e) => e.key === 'Enter' && goToIndex(activeIndex + 1)}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-purple-600 transition-colors cursor-pointer"
                    >
                      Next article
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  ) : <div />}
                </div>
              </section>
            )}

            {/* Keep Learning Row — show after last article */}
            {activeIndex === topic.sections.length - 1 && (
              <KeepLearningRow currentTopicSlug={topicSlug} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookTopic;
